import { useState, useEffect } from 'react'
import { Award, Newspaper, TrendingUp, Link as LinkIcon, RefreshCw, Loader2 } from 'lucide-react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import TrendModal from './TrendModal'
import TrendBadge from './TrendBadge'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import Modal from './Modal'

interface Citation {
  excerpt: string
  source: string
  mentionedBrand: string
  context?: string
  firstSeenAt?: string
  lastSeenAt?: string
  verificationCount?: number
  sourceType?: string
}

interface TopSource {
  domain: string
  count: number
  sourceType: string
}

interface TrendingTopic {
  topic: string
  count: number
}

interface CitationData {
  score: number
  totalCitations: number
  authoritative: number
  newsMentions: number
  topSources: TopSource[]
  sampleCitations?: Citation[]
  trendingTopics?: TrendingTopic[]
  viralMoments?: number
  sourceDiversityScore?: number
  scoreBreakdown?: {
    contextBreakdown?: Record<string, {
      citationCount: number
      percentage: number
    }>
  }
  lastAnalyzed: string
}

interface CitationsCardProps {
  citations: CitationData | null
  brandId: string
  onRefresh?: () => void
}

export default function CitationsCard({ citations, brandId, onRefresh }: CitationsCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [showTrendModal, setShowTrendModal] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [checkingForJobs, setCheckingForJobs] = useState(true)
  const [processingJobId, setProcessingJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<'waiting' | 'active' | 'completed' | 'failed' | null>(null)
  const [historicalData, setHistoricalData] = useState<Array<{weekStartDate: string, totalCitations: number}>>([])
  const { isDemoMode } = useDemoMode()

  // Fetch historical data when not in demo mode
  useEffect(() => {
    if (isDemoMode || !brandId) return

    const fetchHistory = async () => {
      try {
        const response = await api.get(`/visibility/citations/${brandId}/history?limit=6`)
        if (response.data.success && response.data.data) {
          setHistoricalData(response.data.data)
        }
      } catch (error) {
        console.error('Error fetching citation history:', error)
      }
    }

    fetchHistory()
  }, [brandId, isDemoMode])

  // Prepare trend data from real historical data or demo data
  const totalCitations = citations?.totalCitations || 0
  const trendData = isDemoMode || historicalData.length === 0
    ? [
        // Demo trend data (6 months of citation count)
        { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: Math.max(0, totalCitations * 0.6) },
        { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: Math.max(0, totalCitations * 0.7) },
        { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: Math.max(0, totalCitations * 0.8) },
        { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: Math.max(0, totalCitations * 0.88) },
        { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: Math.max(0, totalCitations * 0.94) },
        { date: new Date(Date.now()).toISOString(), value: totalCitations }
      ]
    : [
        // Real historical data (sorted oldest to newest)
        ...historicalData
          .slice()
          .reverse()
          .map(item => ({
            date: item.weekStartDate,
            value: item.totalCitations
          }))
      ]

  // Calculate month-over-month change
  const lastMonthValue = trendData[trendData.length - 2]?.value || totalCitations
  const monthOverMonthChange = totalCitations - lastMonthValue
  const trend = monthOverMonthChange > 0 ? 'up' : monthOverMonthChange < 0 ? 'down' : 'stable'

  // Check for active job on mount
  useEffect(() => {
    const checkForActiveJob = async () => {
      try {
        setCheckingForJobs(true)
        const response = await api.get(`/visibility/citations/${brandId}/active-job`)
        if (response.data.hasActiveJob) {
          setProcessingJobId(response.data.jobId)
          setJobStatus(response.data.status)
          setRefreshing(true)
        }
      } catch (error) {
        console.error('Error checking for active citation job:', error)
      } finally {
        setCheckingForJobs(false)
      }
    }

    checkForActiveJob()
  }, [brandId])

  // Poll for job status with timeout
  useEffect(() => {
    if (!processingJobId) return

    let pollCount = 0
    const maxPolls = 150 // 150 * 2s = 5 minutes max

    const pollInterval = setInterval(async () => {
      pollCount++

      // Timeout after 5 minutes
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval)
        setProcessingJobId(null)
        setRefreshing(false)
        toast.error('Citation analysis timed out. The service may be unavailable.')
        console.warn('[Citations] Job polling timed out after 5 minutes')
        return
      }

      try {
        const response = await api.get(`/visibility/citations/job/${processingJobId}/status`)
        const status = response.data.status

        setJobStatus(status)

        if (status === 'completed') {
          clearInterval(pollInterval)
          setProcessingJobId(null)
          setRefreshing(false)
          toast.success('Citations updated successfully!')

          // Refresh data
          if (onRefresh) {
            onRefresh()
          }
        } else if (status === 'failed') {
          clearInterval(pollInterval)
          setProcessingJobId(null)
          setRefreshing(false)
          toast.error('Citation analysis failed')
        }
      } catch (error) {
        console.error('Error checking job status:', error)
        // If API call fails multiple times, stop polling
        if (pollCount > 5) {
          clearInterval(pollInterval)
          setProcessingJobId(null)
          setRefreshing(false)
          toast.error('Unable to check job status. Please refresh the page.')
        }
      }
    }, 2000) // Poll every 2 seconds

    return () => clearInterval(pollInterval)
  }, [processingJobId, onRefresh])

  const handleRefreshCitations = async () => {
    try {
      setRefreshing(true)

      const response = await api.post(`/visibility/citations/${brandId}/analyze`, { forceRefresh: true })

      const jobId = response.data.jobId
      setProcessingJobId(jobId)
      setJobStatus('waiting')

    } catch (error) {
      console.error('Error refreshing citations:', error)
      toast.error('Failed to refresh citations')
      setRefreshing(false)
    }
  }

  const contextLabels: Record<string, string> = {
    baseline: 'General news',
    industry: 'Industry trends',
    partnerships: 'Partnerships',
    products: 'Product launches',
    funding: 'Funding',
    leadership: 'Leadership'
  }

  const getSourceTypeColor = (type: string) => {
    switch (type) {
      case 'News': return 'bg-purple-100 text-purple-700'
      case 'Industry': return 'bg-blue-100 text-blue-700'
      case 'Academic': return 'bg-green-100 text-green-700'
      case 'Blog': return 'bg-orange-100 text-orange-700'
      case 'Social': return 'bg-pink-100 text-pink-700'
      default: return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <>
      {/* Card */}
      <div
        className="bg-gradient-to-br from-emerald-50 to-white p-4 rounded-lg ring-1 ring-emerald-200 space-y-2 cursor-pointer hover:ring-2 hover:ring-emerald-300 transition-all"
        onClick={() => setModalOpen(true)}
      >
        {/* Row 1: Title + Key Metric */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Award className="w-4 h-4 text-emerald-600" />
            <h3 className="text-sm font-semibold text-gray-900">PR & News</h3>
            <TrendBadge
              hasHistoricalData={true}
              trend={trend}
              value={monthOverMonthChange}
              unit=""
              decimals={0}
              showPlusSign={true}
              onClick={() => setShowTrendModal(true)}
            />
          </div>
          <div className="text-2xl font-bold text-emerald-600">
            {checkingForJobs || refreshing ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              citations?.totalCitations || 0
            )}
          </div>
        </div>

        {/* Row 2: Takeaway/Analysis */}
        <p className="text-xs text-gray-600 mb-2">
          {!citations ? "No PR or news coverage tracked yet." : (() => {
            const total = citations?.totalCitations
            const authoritative = citations?.authoritative
            const news = citations?.newsMentions
            if (total === 0) return "No PR or news coverage found yet."
            if (authoritative >= 5) return "Strong media presence with authoritative sources."
            if (news >= 3) return "Good news coverage building brand awareness."
            if (total >= 3) return "Growing media presence - continue building."
            return "Limited media coverage - opportunity to improve."
          })()}
        </p>

        {/* Row 3: What was analyzed and result */}
        <p className="text-xs text-gray-500 mb-2">
          {checkingForJobs ? "Checking analysis status..." :
            refreshing ? "Analyzing citations and news mentions..." :
            !citations ? "Click to run PR & News analysis" :
            `${citations?.authoritative || 0} authoritative citations • ${citations?.newsMentions || 0} news mentions${citations?.topSources && citations?.topSources.length > 0 ? ` • Featured in ${citations?.topSources[0].domain}` : ''}`}
        </p>

        {/* Row 4: Context breakdown - WHAT TOPICS brand is mentioned in */}
        {(() => {
          const contextBreakdown = citations?.scoreBreakdown?.contextBreakdown
          if (!contextBreakdown) return null

          const contexts = Object.entries(contextBreakdown)
            .map(([name, data]) => ({
              name,
              citationCount: data.citationCount
            }))
            .filter(c => c.citationCount > 0)
            .sort((a, b) => b.citationCount - a.citationCount)
            .slice(0, 3)

          if (contexts.length === 0) return null

          return (
            <div className="flex flex-wrap gap-1 mt-1">
              <span className="text-xs text-gray-500">Mentioned in:</span>
              {contexts.map((ctx, idx) => (
                <span key={ctx.name} className="text-xs font-medium text-emerald-700">
                  {contextLabels[ctx.name] || ctx.name} ({ctx.citationCount})
                  {idx < contexts.length - 1 && <span className="text-gray-400 mx-1">•</span>}
                </span>
              ))}
            </div>
          )
        })()}
      </div>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="5xl">
        <Modal.Header onClose={() => setModalOpen(false)}>
          <Award className="w-6 h-6 text-emerald-600" />
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">PR & News Citations</h2>
            {!refreshing && !jobStatus && (
              <p className="text-sm text-gray-600 mt-1">
                {citations?.totalCitations || 0} total citations found across news and media sources
              </p>
            )}
          </div>
          {/* Refresh button only shown in development */}
          {import.meta.env.DEV && (
            <button
              onClick={handleRefreshCitations}
              disabled={refreshing}
              className="p-2 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh citations (dev only)"
            >
              <RefreshCw className={`w-5 h-5 text-emerald-600`} />
            </button>
          )}
        </Modal.Header>
        <Modal.Body>

            {/* Processing Banner */}
            {refreshing && jobStatus && (
              <div className="bg-emerald-50 border-b border-emerald-200 rounded-lg p-6 m-8">
                <div className="space-y-4 text-center">
                  <Loader2 className="w-8 h-8 text-emerald-600 animate-spin text-center mx-auto"  />
                  <p className="text-lg font-bold text-emerald-900">
                    {jobStatus === 'waiting' && 'We are processing and researching...'}
                    {jobStatus === 'active' && 'Analyzing citations and news mentions...'}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    This runs in the background and could take a few minutes. You can leave and come back.
                  </p>
                </div>
              </div>
            )}

            {/* Modal Content */}
            {!refreshing && (
              <div className="p-6 space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 p-4 rounded-lg">
                  <div className="text-xs text-emerald-600 font-medium uppercase tracking-wide mb-1">
                    Total Citations
                  </div>
                  <div className="text-2xl font-bold text-emerald-700">
                    {citations?.totalCitations || 0}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1">
                    Authoritative
                  </div>
                  <div className="text-2xl font-bold text-purple-700">
                    {citations?.authoritative || 0}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">
                    News Mentions
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    {citations?.newsMentions || 0}
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">
                    Citation Score
                  </div>
                  <div className="text-2xl font-bold text-orange-700">
                    {citations?.score || 0}/100
                  </div>
                </div>
              </div>

              {/* Top Sources */}
              {citations?.topSources && citations?.topSources.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <LinkIcon className="w-4 h-4" />
                    <span>Top Media Sources ({citations?.topSources.length})</span>
                  </h3>
                  <div className="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden">
                    <div className="max-h-64 overflow-y-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Domain
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Citations
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Source Type
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {citations?.topSources.map((source, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {source.domain}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {source.count}
                              </td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getSourceTypeColor(source.sourceType)}`}>
                                  {source.sourceType}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Context Breakdown */}
              {citations?.scoreBreakdown?.contextBreakdown && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>Citation Topics</span>
                  </h3>
                  {(() => {
                    const citationTopics = Object.entries(citations?.scoreBreakdown.contextBreakdown)
                      .filter(([_, data]) => data.citationCount > 0)
                      .sort(([_, a], [__, b]) => b.citationCount - a.citationCount)

                    if (citationTopics.length === 0) {
                      return (
                        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                          <p className="text-sm text-gray-400">
                            No citation topics found
                          </p>
                        </div>
                      )
                    }

                    return (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {citationTopics.map(([context, data]) => (
                          <div
                            key={context}
                            className="bg-white border border-gray-200 rounded-lg p-3 hover:border-emerald-300 transition-colors"
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-gray-900">
                                {contextLabels[context] || context}
                              </span>
                              <span className="text-xs font-semibold text-emerald-600">
                                {data.citationCount}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {data.percentage.toFixed(1)}% of total
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  })()}
                </div>
              )}

              {/* Sample Citations */}
              {citations?.sampleCitations && citations?.sampleCitations.length > 0 ? (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                    <Newspaper className="w-4 h-4" />
                    <span>Sample Citations ({citations?.sampleCitations.length})</span>
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {citations?.sampleCitations.map((citation, idx) => {
                      const isUrl = citation.source && (citation.source.startsWith('http://') || citation.source.startsWith('https://'))
                      const isUnknown = citation.source === 'Unknown' || citation.source === 'General mention'

                      return (
                        <div
                          key={idx}
                          className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:border-emerald-300 transition-colors"
                        >
                          <p className="text-sm text-gray-700 italic mb-3">
                            "{citation.excerpt}"
                          </p>
                          <div className="flex items-center justify-between text-xs mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-500">Source:</span>
                              {isUrl ? (
                                <a
                                  href={citation.source}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-emerald-600 hover:text-emerald-700 hover:underline font-medium flex items-center gap-1"
                                >
                                  <LinkIcon className="w-3 h-3" />
                                  {new URL(citation.source).hostname}
                                </a>
                              ) : (
                                <span className={isUnknown ? "text-gray-400 italic" : "font-medium text-gray-700"}>
                                  {citation.source}
                                </span>
                              )}
                            </div>
                            {citation.context && (
                              <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                                {contextLabels[citation.context] || citation.context}
                              </span>
                            )}
                          </div>
                          {/* Citation Metadata */}
                          {(citation.verificationCount || citation.firstSeenAt || citation.lastSeenAt) && (
                            <div className="flex items-center gap-4 text-xs text-gray-500 border-t border-gray-200 pt-2 mt-2">
                              {citation.verificationCount && citation.verificationCount > 1 && (
                                <div className="flex items-center gap-1">
                                  <RefreshCw className="w-3 h-3" />
                                  <span>Verified {citation.verificationCount}x</span>
                                </div>
                              )}
                              {citation.firstSeenAt && (
                                <span>First: {new Date(citation.firstSeenAt).toLocaleDateString()}</span>
                              )}
                              {citation.lastSeenAt && (
                                <span>Last: {new Date(citation.lastSeenAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              ) : (citations?.totalCitations ?? 0) > 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                  <Newspaper className="w-12 h-12 text-yellow-400 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-gray-900 mb-2">
                    Citation Details Not Available
                  </h3>
                  <p className="text-xs text-gray-600 mb-3">
                    {citations?.totalCitations} citations were found, but detailed excerpts and sources are not available for this analysis.
                    Re-run the citation analysis to collect detailed citation data with source links.
                  </p>
                  <p className="text-xs text-gray-500">
                    This can happen with older citation data that was collected before the detailed citation tracking feature was added.
                  </p>
                </div>
              ) : null}

              {/* Trending Topics */}
              {citations?.trendingTopics && citations?.trendingTopics.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">
                    Trending Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {citations?.trendingTopics.map((topic, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full"
                      >
                        {topic.topic} ({topic.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Trend Modal */}
      <TrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title="PR & News Citations Trend"
        metricName="Total Citations"
        data={trendData}
        color="#10b981"
        unit=""
      />
    </>
  )
}
